import { __assign, __rest, __spreadArray } from "tslib";
/* eslint-disable no-restricted-imports */
// ^ Wrapping `react-select` in a core component is the desired use case
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Creatable from 'react-select/creatable';
import uniqueId from '../utilities/uniqueId';
import './EmailRecipientsInput.scss';
/* eslint-enable import/named */
var ADD_VALUE_KEY_CODES = ['Comma', 'Enter', 'Return', 'Space', 'Tab'];
// react-select uses Emotion for CSS-in-JS styles, and provides no way
// to disable it. You can override each style via functions that return
// style objects, so this is a hack to make every style function return
// an empty object. https://github.com/JedWatson/react-select/issues/2872
var styleProxy = new Proxy({}, {
    get: function () { return function () { }; }
});
var EmailRecipientsInput = function (_a) {
    var className = _a.className, description = _a.description, errorMessage = _a.errorMessage, isDisabled = _a.isDisabled, label = _a.label, onChange = _a.onChange, placeholder = _a.placeholder, value = _a.value, props = __rest(_a, ["className", "description", "errorMessage", "isDisabled", "label", "onChange", "placeholder", "value"]);
    var _b = useState(''), inputValue = _b[0], setInputValue = _b[1];
    var recipients = value || [];
    // Since we use the keyDown handler to add value when keys such as
    // 'comma, tab, space' are pressed, this change handler effectively
    // only handles the 'delete' action
    var handleChange = function (newRecipients) {
        if (newRecipients == null) {
            onChange(null);
        }
        else if (!isDisabled && newRecipients) {
            onChange((newRecipients === null || newRecipients === void 0 ? void 0 : newRecipients.length) > 0
                ? newRecipients.map(function (recipient) { return recipient.value; })
                : null);
        }
    };
    // Since we know that email addresses can't contain spaces, commas, tabs, etc.,
    // we can automatically create a token for the entered value when any of these
    // keys are pressed.
    var handleKeyDown = function (event) {
        if (inputValue && ADD_VALUE_KEY_CODES.includes(event.nativeEvent.code)) {
            onChange(__spreadArray(__spreadArray([], recipients, true), [inputValue], false));
            setInputValue('');
            event.preventDefault();
        }
    };
    // If any new value exist when the field is blurred, add them to the list.
    var handleBlur = function (event) {
        var newRecipient = event.target.value;
        if (newRecipient) {
            onChange(__spreadArray(__spreadArray([], recipients, true), [newRecipient], false));
        }
    };
    var containerClass = classnames('EmailRecipientsInput', className, {
        'EmailRecipientsInput--isInvalid': Boolean(errorMessage)
    });
    var inputId = useRef(uniqueId('input'));
    return (React.createElement("div", __assign({}, props, { className: containerClass }),
        label && (React.createElement("label", { className: 'EmailRecipientsInput__label', htmlFor: inputId.current }, label)),
        React.createElement(Creatable, { classNamePrefix: 'EmailRecipientsInput', components: { DropdownIndicator: null }, inputId: inputId.current, inputValue: inputValue, isClearable: false, isDisabled: isDisabled, isMulti: true, menuIsOpen: false, onBlur: handleBlur, onChange: handleChange, onInputChange: setInputValue, onKeyDown: handleKeyDown, placeholder: placeholder, styles: styleProxy, 
            // react-select wants to work off of arrays of objects; we want our
            // component to work off of arrays of strings; so here we translate.
            value: recipients.map(function (recipient) { return ({
                label: recipient,
                value: recipient
            }); }) }),
        errorMessage && (React.createElement("div", { className: 'TextInput__errorMessage', role: 'alert' }, errorMessage)),
        description && (React.createElement("div", { className: 'EmailRecipientsInput__description' }, description))));
};
EmailRecipientsInput.defaultProps = {
    isDisabled: false,
    onBlur: function () { },
    placeholder: 'Enter email addresses...'
};
EmailRecipientsInput.propTypes = {
    /** Class name to render on the root node. */
    className: PropTypes.string,
    /** Description content to render below the input. */
    description: PropTypes.node,
    /** Error content to render below the input (also triggers invalid UI state). */
    errorMessage: PropTypes.node,
    /** Whether the input is disabled. */
    isDisabled: PropTypes.bool,
    /** Label content to render above the input. */
    label: PropTypes.node,
    onBlur: PropTypes.func,
    /** Callback function called with new value on change. */
    onChange: PropTypes.func.isRequired,
    /** Placeholder text to render when no value exist. */
    placeholder: PropTypes.string,
    /**
     * The value of the input. Although `values` is a more logical name for this prop,
     * we name it `value` to be consistent across all form components
     * and for a more seamless integration with react-hook-form in FormEmailRecipientsInput.
     */
    value: PropTypes.arrayOf(PropTypes.string.isRequired)
};
export default EmailRecipientsInput;
//# sourceMappingURL=EmailRecipientsInput.js.map